/**
 * Inquiry Detail Page
 *
 * Displays full inquiry details with items, quote, and messages
 * Tasks: T5.2.5, T5.2.6 - Inquiry Detail and Quote View
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Box,
  Spinner,
  Banner,
  TextField,
  DataTable,
  Divider,
  Modal,
} from '@shopify/polaris';
import {
  getInquiry,
  addMessage,
  acceptQuote,
  declineQuote,
  requestRevision,
  type InquiryDetail,
  type InquiryStatus,
} from '../../services/inquiry.service';
import { useUIStore } from '../../store/uiStore';

const STATUS_BADGES: Record<InquiryStatus, { tone: 'info' | 'warning' | 'success' | 'critical' | 'attention'; label: string }> = {
  PENDING: { tone: 'info', label: 'Pending' },
  UNDER_REVIEW: { tone: 'attention', label: 'Under Review' },
  QUOTE_SENT: { tone: 'warning', label: 'Quote Sent' },
  QUOTE_ACCEPTED: { tone: 'success', label: 'Quote Accepted' },
  QUOTE_DECLINED: { tone: 'critical', label: 'Quote Declined' },
  QUOTE_REVISION_REQUESTED: { tone: 'attention', label: 'Revision Requested' },
  IN_PROGRESS: { tone: 'info', label: 'In Progress' },
  COMPLETED: { tone: 'success', label: 'Completed' },
  CANCELLED: { tone: 'critical', label: 'Cancelled' },
};

export function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useUIStore();

  const [inquiry, setInquiry] = useState<InquiryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Quote action modals
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInquiry = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await getInquiry(id);
      setInquiry(data);
    } catch (err) {
      setError('Failed to load inquiry');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInquiry();
  }, [fetchInquiry]);

  const handleSendMessage = useCallback(async () => {
    if (!id || !newMessage.trim()) return;

    const messageText = newMessage.trim();
    setSendingMessage(true);
    setNewMessage(''); // Important Issue #3 Fix: Clear input immediately for better UX

    try {
      const sentMessage = await addMessage(id, messageText);

      // Important Issue #3 Fix: Optimistic update - add message to local state
      // This prevents race conditions and avoids full refetch
      setInquiry((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: [
            ...prev.messages,
            {
              id: sentMessage.id,
              senderType: 'USER' as const,
              message: sentMessage.message,
              createdAt: sentMessage.createdAt,
            },
          ],
        };
      });

      showToast('Message sent', 'success');
    } catch (err) {
      // Restore message text on failure so user can retry
      setNewMessage(messageText);
      showToast('Failed to send message', 'error');
      console.error('Message send error:', err);
    } finally {
      setSendingMessage(false);
    }
  }, [id, newMessage, showToast]);

  const handleAcceptQuote = useCallback(async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const updated = await acceptQuote(id);
      setInquiry(updated);
      showToast('Quote accepted!', 'success');
    } catch (err) {
      showToast('Failed to accept quote', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [id, showToast]);

  const handleDeclineQuote = useCallback(async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const updated = await declineQuote(id, actionReason);
      setInquiry(updated);
      setDeclineModalOpen(false);
      setActionReason('');
      showToast('Quote declined', 'info');
    } catch (err) {
      showToast('Failed to decline quote', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [id, actionReason, showToast]);

  const handleRequestRevision = useCallback(async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const updated = await requestRevision(id, actionReason);
      setInquiry(updated);
      setRevisionModalOpen(false);
      setActionReason('');
      showToast('Revision requested', 'success');
    } catch (err) {
      showToast('Failed to request revision', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [id, actionReason, showToast]);

  if (loading) {
    return (
      <Page title="Loading...">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
          </BlockStack>
        </Box>
      </Page>
    );
  }

  if (error || !inquiry) {
    return (
      <Page title="Error">
        <Banner tone="critical">{error || 'Inquiry not found'}</Banner>
        <Box paddingBlockStart="400">
          <Button onClick={() => navigate('/inquiries')}>Back to Inquiries</Button>
        </Box>
      </Page>
    );
  }

  const statusBadge = STATUS_BADGES[inquiry.status];

  return (
    <Page
      title={`Inquiry ${inquiry.referenceNo}`}
      backAction={{ content: 'Inquiries', onAction: () => navigate('/inquiries') }}
      titleMetadata={<Badge tone={statusBadge.tone}>{statusBadge.label}</Badge>}
    >
      <Layout>
        {/* Inquiry Info */}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Inquiry Details</Text>
                <Divider />
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">Type</Text>
                  <Badge tone={inquiry.type === 'SAMPLE_REQUEST' ? 'info' : 'attention'}>
                    {inquiry.type === 'SAMPLE_REQUEST' ? 'Sample' : 'Bulk Order'}
                  </Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">Created</Text>
                  <Text as="span" variant="bodySm">
                    {new Date(inquiry.createdAt).toLocaleDateString('en-IN')}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">Items</Text>
                  <Text as="span" variant="bodySm">{inquiry.items.length}</Text>
                </InlineStack>
              </BlockStack>
            </Card>

            {inquiry.deliveryAddress && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Delivery Address</Text>
                  <Text as="p" variant="bodySm">{inquiry.deliveryAddress}</Text>
                </BlockStack>
              </Card>
            )}

            {inquiry.logoDescription && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Branding Requirements</Text>
                  <Text as="p" variant="bodySm">{inquiry.logoDescription}</Text>
                </BlockStack>
              </Card>
            )}

            {inquiry.notes && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">Additional Notes</Text>
                  <Text as="p" variant="bodySm">{inquiry.notes}</Text>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>

        {/* Items & Quote */}
        <Layout.Section>
          <BlockStack gap="400">
            {/* Items */}
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Items</Text>
                {inquiry.items.map((item) => (
                  <Box key={item.id} padding="300" background="bg-surface-secondary" borderRadius="200">
                    <InlineStack gap="400" blockAlign="center">
                      {item.product.primaryImage && (
                        <img
                          src={item.product.primaryImage}
                          alt={item.product.name}
                          style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <Text as="h3" variant="bodySm" fontWeight="semibold">{item.product.name}</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          SKU: {item.product.sku} | Qty: {item.quantity}
                          {item.color && ` | Color: ${item.color}`}
                        </Text>
                        {item.notes && (
                          <Text as="p" variant="bodySm">Note: {item.notes}</Text>
                        )}
                      </div>
                    </InlineStack>
                  </Box>
                ))}
              </BlockStack>
            </Card>

            {/* Quote */}
            {inquiry.quote && (
              <QuoteSection
                quote={inquiry.quote}
                inquiryStatus={inquiry.status}
                onAccept={handleAcceptQuote}
                onDecline={() => setDeclineModalOpen(true)}
                onRequestRevision={() => setRevisionModalOpen(true)}
                loading={actionLoading}
              />
            )}

            {/* Messages */}
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Messages</Text>

                {inquiry.messages.length === 0 ? (
                  <Text as="p" variant="bodySm" tone="subdued">
                    No messages yet. Start a conversation with our team.
                  </Text>
                ) : (
                  <BlockStack gap="300">
                    {inquiry.messages.map((msg) => (
                      <Box
                        key={msg.id}
                        padding="300"
                        background={msg.senderType === 'USER' ? 'bg-surface-info' : 'bg-surface-secondary'}
                        borderRadius="200"
                      >
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <Text as="span" variant="bodySm" fontWeight="semibold">
                              {msg.senderType === 'USER' ? 'You' : 'Support Team'}
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                              {new Date(msg.createdAt).toLocaleString('en-IN')}
                            </Text>
                          </InlineStack>
                          <Text as="p" variant="bodySm">{msg.message}</Text>
                        </BlockStack>
                      </Box>
                    ))}
                  </BlockStack>
                )}

                <Divider />

                <InlineStack gap="200">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label="New message"
                      labelHidden
                      value={newMessage}
                      onChange={setNewMessage}
                      placeholder="Type your message..."
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleSendMessage}
                    loading={sendingMessage}
                    disabled={!newMessage.trim()}
                  >
                    Send
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>

      {/* Decline Modal */}
      <Modal
        open={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        title="Decline Quote"
        primaryAction={{
          content: 'Decline',
          onAction: handleDeclineQuote,
          loading: actionLoading,
          destructive: true,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setDeclineModalOpen(false) }]}
      >
        <Modal.Section>
          <TextField
            label="Reason (optional)"
            value={actionReason}
            onChange={setActionReason}
            multiline={3}
            placeholder="Tell us why you're declining this quote..."
            autoComplete="off"
          />
        </Modal.Section>
      </Modal>

      {/* Revision Modal */}
      <Modal
        open={revisionModalOpen}
        onClose={() => setRevisionModalOpen(false)}
        title="Request Quote Revision"
        primaryAction={{
          content: 'Request Revision',
          onAction: handleRequestRevision,
          loading: actionLoading,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setRevisionModalOpen(false) }]}
      >
        <Modal.Section>
          <TextField
            label="What changes would you like?"
            value={actionReason}
            onChange={setActionReason}
            multiline={3}
            placeholder="Describe what changes you'd like to the quote..."
            autoComplete="off"
          />
        </Modal.Section>
      </Modal>
    </Page>
  );
}

interface QuoteSectionProps {
  quote: InquiryDetail['quote'];
  inquiryStatus: InquiryStatus;
  onAccept: () => void;
  onDecline: () => void;
  onRequestRevision: () => void;
  loading: boolean;
}

function QuoteSection({
  quote,
  inquiryStatus,
  onAccept,
  onDecline,
  onRequestRevision,
  loading,
}: QuoteSectionProps) {
  if (!quote) return null;

  const isQuoteSent = inquiryStatus === 'QUOTE_SENT';
  const isExpired = new Date(quote.validityDate) < new Date();

  const rows = quote.items.map((item) => [
    item.product.name,
    item.product.sku,
    item.quantity.toString(),
    `₹${item.unitPrice.toLocaleString('en-IN')}`,
    `₹${item.total.toLocaleString('en-IN')}`,
  ]);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">Quote</Text>
          <Badge tone={quote.status === 'ACCEPTED' ? 'success' : quote.status === 'DECLINED' ? 'critical' : 'info'}>
            {quote.status}
          </Badge>
        </InlineStack>

        {isExpired && isQuoteSent && (
          <Banner tone="warning">
            This quote has expired. Please contact us for a new quote.
          </Banner>
        )}

        <DataTable
          columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric']}
          headings={['Product', 'SKU', 'Qty', 'Unit Price', 'Total']}
          rows={rows}
          totals={['', '', '', 'Grand Total:', `₹${quote.totalAmount.toLocaleString('en-IN')}`]}
        />

        <InlineStack align="space-between">
          <Text as="p" variant="bodySm" tone="subdued">
            Valid until: {new Date(quote.validityDate).toLocaleDateString('en-IN')}
          </Text>
          {quote.pdfUrl && (
            <Button url={quote.pdfUrl} external>
              Download PDF
            </Button>
          )}
        </InlineStack>

        {isQuoteSent && !isExpired && (
          <>
            <Divider />
            <InlineStack gap="200">
              <Button variant="primary" onClick={onAccept} loading={loading}>
                Accept Quote
              </Button>
              <Button onClick={onRequestRevision} loading={loading}>
                Request Revision
              </Button>
              <Button tone="critical" onClick={onDecline} loading={loading}>
                Decline
              </Button>
            </InlineStack>
          </>
        )}
      </BlockStack>
    </Card>
  );
}
